import { useMemo } from "react";

import { calculateFile } from "~served/utils/calculations";

import {
	useTillCreateFileInput,
	useTillFileInputSnapshot,
	useTillUpdateFileInput,
} from "~/store";
import {
	FileProductInput,
	FileProductOptionInput,
} from "~/types/__generated/gql/graphql";
import {
	FileInputLike,
	FileProductInputLike,
	FileProductOptionInputLike,
} from "~served/utils/calculations/types";
import { DEFAULT_CURRENCY_CODE } from "~served/configs/constants";
import { useGetCurrentCompany } from "~/queries/useGetCurrentCompany";
import { useGetProductOptions } from "~/queries/useGetProductOptions";
import { useGetProducts } from "~/queries/useGetProducts";

export const useTillSummary = () => {
	const createFileInput = useTillCreateFileInput();
	const updateFileInput = useTillUpdateFileInput();
	const snapshotFileInput = useTillFileInputSnapshot();
	const { data: company } = useGetCurrentCompany();
	const { data: products = [] } = useGetProducts();
	const { data: productOptions = [] } = useGetProductOptions();

	const getSelectedCurrency = () => {
		if (createFileInput)
			return createFileInput.multiCurrencyConfig?.selectedCurrency;
		if (updateFileInput)
			return updateFileInput.multiCurrencyConfig?.selectedCurrency;
		return DEFAULT_CURRENCY_CODE;
	};

	const getMultiCurrencyConfig = () => {
		if (!company?.currencyConfigs) return null;
		const configs = company.currencyConfigs.map((c) => ({
			code: c.code,
			rate: c.rate,
			isPrimaryCurrency: c.isPrimaryCurrency,
		}));
		return { currencyConfigs: configs };
	};

	const getProductMultiCurrencyPriceConfig = (product: FileProductInput) => {
		if (!product.multiCurrencyConfig) return null;
		const matched = products.find((p) => p._id === product.product);
		if (matched) {
			return { priceConfigs: matched.priceConfigs };
		} else {
			return { priceConfigs: product.multiCurrencyConfig.priceConfigs };
		}
	};

	const getOptionMultiCurrencyPriceConfig = (
		option: FileProductOptionInput,
	) => {
		if (!option.multiCurrencyConfig) return null;
		const matched = productOptions.find((o) => o._id === option.option);
		if (matched) {
			return { priceConfigs: matched.priceConfigs };
		} else {
			return { priceConfigs: option.multiCurrencyConfig.priceConfigs };
		}
	};

	const createFileInputSummary = useMemo(
		() =>
			calculateFile({
				products: createFileInput?.products ?? [],
				cancelledProducts: createFileInput?.cancelledProducts ?? [],
			} as unknown as FileInputLike<
				FileInputLike,
				FileProductInputLike,
				FileProductOptionInputLike
			>),
		[createFileInput],
	);

	const updateFileInputSummary = useMemo(
		() =>
			calculateFile({
				products: updateFileInput?.products ?? [],
				cancelledProducts: updateFileInput?.cancelledProducts ?? [],
			} as unknown as FileInputLike<
				FileInputLike,
				FileProductInputLike,
				FileProductOptionInputLike
			>),
		[updateFileInput],
	);

	const snapshotFileInputSummary = useMemo(() => {
		const currency = getSelectedCurrency();
		calculateFile({
			products: snapshotFileInput?.products
				? snapshotFileInput?.products.map((p) => ({
						...p,
						multiCurrencyConfig: {
							...getProductMultiCurrencyPriceConfig(p),
							selectedCurrency: currency,
						},
						options: p.options.map((o) => ({
							...o,
							multiCurrencyConfig: {
								...getOptionMultiCurrencyPriceConfig(o),
								selectedCurrency: currency,
							},
						})),
					}))
				: [],
			cancelledProducts: snapshotFileInput?.cancelledProducts
				? snapshotFileInput?.cancelledProducts.map((p) => ({
						...p,
						multiCurrencyConfig: {
							...getProductMultiCurrencyPriceConfig(p),
							selectedCurrency: currency,
						},
						options: p.options.map((o) => ({
							...o,
							multiCurrencyConfig: {
								...getOptionMultiCurrencyPriceConfig(o),
								selectedCurrency: currency,
							},
						})),
					}))
				: [],
			multiCurrencyConfig: {
				...getMultiCurrencyConfig(),
				selectedCurrency: currency,
			},
		} as unknown as FileInputLike<
			FileInputLike,
			FileProductInputLike,
			FileProductOptionInputLike
		>);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [snapshotFileInput, products, productOptions]);

	const tillItemsSummary = useMemo(() => {
		const currency = getSelectedCurrency();
		const editFileInput = createFileInput ? createFileInput : updateFileInput;
		const added: FileProductInput[] = [] as FileProductInput[];
		const removed: FileProductInput[] = [] as FileProductInput[];

		for (const product of editFileInput?.products ?? []) {
			const matched = snapshotFileInput?.products?.find(
				(p) => p._id === product._id,
			);
			// if matched then both snapshot and on till has the product
			// else then the file on till has but not on snapshot, thus add.
			if (matched) {
				if (product.quantity > matched.quantity) {
					added.push({
						...matched,
						quantity: Math.abs(matched.quantity - product.quantity),
					});
				} else if (matched.quantity > product.quantity) {
					removed.push({
						...matched,
						quantity: Math.abs(matched.quantity - product.quantity),
					});
				}
			} else {
				added.push(product);
			}
		}

		return {
			prev: calculateFile({
				products: snapshotFileInput?.products
					? snapshotFileInput?.products.map((p) => ({
							...p,
							multiCurrencyConfig: {
								...getProductMultiCurrencyPriceConfig(p),
								selectedCurrency: currency,
							},
							options: p.options.map((o) => ({
								...o,
								multiCurrencyConfig: {
									...getOptionMultiCurrencyPriceConfig(o),
									selectedCurrency: currency,
								},
							})),
						}))
					: [],
				cancelledProducts: snapshotFileInput?.cancelledProducts
					? snapshotFileInput?.cancelledProducts.map((p) => ({
							...p,
							multiCurrencyConfig: {
								...getProductMultiCurrencyPriceConfig(p),
								selectedCurrency: currency,
							},
							options: p.options.map((o) => ({
								...o,
								multiCurrencyConfig: {
									...getOptionMultiCurrencyPriceConfig(o),
									selectedCurrency: currency,
								},
							})),
						}))
					: [],
				multiCurrencyConfig: {
					...getMultiCurrencyConfig(),
					selectedCurrency: currency,
				},
			} as unknown as FileInputLike<
				FileInputLike,
				FileProductInputLike,
				FileProductOptionInputLike
			>),
			add: calculateFile({
				products: added ?? [],
				cancelledProducts: [],
			} as unknown as FileInputLike<
				FileInputLike,
				FileProductInputLike,
				FileProductOptionInputLike
			>),
			remove: calculateFile({
				products: removed ?? [],
				cancelledProducts: [],
			} as unknown as FileInputLike<
				FileInputLike,
				FileProductInputLike,
				FileProductOptionInputLike
			>),
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [snapshotFileInput, createFileInput, updateFileInput]);

	return {
		createFileInputSummary,
		updateFileInputSummary,
		snapshotFileInputSummary,
		tillItemsSummary,
	};
};
